import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { useRouter } from 'next/router';

import TabsScrollTo from '@com/shared/ocs/tab-scroll-to';
import Divider from '@com/_atoms/Divider';
import ViewMore from '@com/shared/ocs/viewMore';
import { colors } from '@configs/Theme';
import {
  useGetDoctorActivityStatus,
  useGetDoctorReviewsInfinite,
  useGetDoctorProfile,
  useGetDoctorServiceTarifs,
} from '@api/ocs/doctor/index.rq';
import SuggestedDoctorsCarousel from '@containers/doctor/profile/[id]/_components/suggested-doctor-carousel.tsx/suggested-doctor-carousel';
import useInView from '@hooks/useInView';
import { MainLayout } from '@com/Layout';
import BackIcon from '@com/shared/ocs/back-icon';
import { useGetUserServiceByDoctorId } from '@api/profile/index.rq';
import { trackDoctorViewed } from '@utilities/analytics/activity';
import { useReport } from '@utilities/analytics/report';

import DoctorFAQ from './faqs/faqs';
import Comments from './comments/comments';
import { RateCardProps } from './rateCard/rateCard';
import DoctorRating from './doctorRating/doctorRating';
import ConsultantionTypes from './consultantionTypes/consultantionTypes';
import DoctorInfoShimer from './doctorInfo/doctor-info-shimer';
import OfficeDetail from './office-detail';
import ServicesCarousel from './services-carousel';
import HealthServices from './health-services';

const DoctorInfo = dynamic(() => import('./doctorInfo/doctorInfo'));

const Insurances = dynamic(() => import('./insurances'));

type DoctorContainerProps = {
  doctorId: number;
};

const DoctorContainer = ({ doctorId }: DoctorContainerProps) => {
  const { query } = useRouter();

  const isFromNobat = query?.fromNobat;

  const { ref: profileReviewsRef, isInView: profileReviewsIsInView } =
    useInView();

  const { data: doctorProfileResponse, isLoading } =
    useGetDoctorProfile(doctorId);

  const doctorProfileResponseData = doctorProfileResponse?.result;

  useReport(
    doctorProfileResponseData
      ? `${doctorId}:${doctorProfileResponseData.recId}`
      : undefined,
    () => trackDoctorViewed(doctorId, doctorProfileResponseData),
  );

  const { data: doctorActivityStatus } = useGetDoctorActivityStatus(doctorId);

  const isDoctorOnline = doctorActivityStatus?.activityStatus;

  const shouldShowNotifyCard =
    !isFromNobat && !isDoctorOnline && !doctorProfileResponseData?.hasSchedule;

  const {
    data: serviceTarifsData,
    isLoading: isServiceTarifsLoading,
    isPending: isServiceTarifsPending,
  } = useGetDoctorServiceTarifs(
    doctorProfileResponseData?.recId,
    doctorProfileResponseData?.subLatestSpecialityId,
    doctorProfileResponseData?.systemType,
    doctorProfileResponseData?.hasSchedule,
    { enabled: !!doctorProfileResponseData?.recId },
  );

  const hasActiveServices =
    serviceTarifsData?.result?.some((service) => service.isActive) ?? false;

  const { data: userServiceData, isLoading: userServiceDataIsLoading } =
    useGetUserServiceByDoctorId(
      {
        id: doctorId,
        pageNumber: 1,
        pageSize: 50,
      },
      {
        enabled: !!doctorId,
      },
    );

  const showConsultationSection =
    isServiceTarifsLoading ||
    isServiceTarifsPending ||
    hasActiveServices ||
    shouldShowNotifyCard;

  const tabsLabels = useMemo(() => {
    const tabs = [];

    if (showConsultationSection) {
      tabs.push({
        label: 'نوع ویزیت',
        id: 'consultantionTypes',
      });
    }

    tabs.push(
      { label: 'درباره پزشک', id: 'aboutDoctor' },
      { label: 'اطلاعات مطب', id: 'officeDetail' },
      { label: 'نظرات', id: 'doctorReviews' },
    );

    return tabs;
  }, [showConsultationSection]);

  const {
    data: doctorProfileReviews,
    fetchNextPage,
    hasNextPage,
  } = useGetDoctorReviewsInfinite(
    { Id: doctorId },
    { enabled: profileReviewsIsInView },
  );

  const REVIEW_COUNT = doctorProfileReviews?.pages[0].totalCount;

  const generateRatingCards: RateCardProps[] = [
    {
      iconName: 'Star',
      iconColor: colors.yellow[400],
      text: `${doctorProfileResponseData?.rate}`,
      label: doctorProfileResponseData?.rate
        ? REVIEW_COUNT > 0
          ? `(از ${REVIEW_COUNT} نظر)`
          : 'نظر پزشک'
        : 'بدون نظر',
    },
    {
      iconName: 'CircleCheck',
      iconColor: colors.green[400],
      label: 'مشاوره موفق',
    },
    {
      iconName: 'StickyNote',
      iconColor: colors.blue[400],
      label: 'نسخه الکترونیک',
    },
  ];

  const PSYCHOLOGY_SPECIALITY_ID = 7;

  const medicalNumberLabel =
    doctorProfileResponseData?.latestSpecialityId === PSYCHOLOGY_SPECIALITY_ID
      ? ' با کد نظام روانشناسی'
      : ' با کد نظام پزشکی';

  const aboutDoctor = !!doctorProfileResponseData?.aboutDoctor
    ? doctorProfileResponseData.aboutDoctor
    : `
   ${doctorProfileResponseData?.fullName} ${doctorProfileResponseData?.latestSpecialityName} ${medicalNumberLabel} ${doctorProfileResponseData?.medicalNumber}، اینجا هستند تا به شما کمک کنند پاسخ سوالات پزشکی‌تان را سریع، دقیق و قابل اعتماد دریافت کنید.
  `;

  return (
    <MainLayout
      title="پروفایل پزشک"
      rightIcon={<BackIcon />}
      hasHeader
      headerType="withoutLogo"
      scrollToTop
    >
      <div className="relative">
        {isLoading ? (
          <DoctorInfoShimer />
        ) : (
          <div className="flex flex-col gap-4 w-full">
            <DoctorInfo
              avatarSrc={doctorProfileResponseData?.imageUrl as string}
              name={doctorProfileResponseData?.fullName}
              expertise={doctorProfileResponseData?.latestSpecialityName}
              medicalCodeSystem={doctorProfileResponseData?.medicalNumber}
              activity={isDoctorOnline}
              historyCount={doctorProfileResponseData?.doctorExperienceDate}
              clinicCities={doctorProfileResponseData?.cities}
              doctorId={doctorId}
            />

            <DoctorRating cards={generateRatingCards} />
          </div>
        )}

        <TabsScrollTo labels={tabsLabels} />

        {showConsultationSection && (
          <ConsultantionTypes
            isFromNobat={!!isFromNobat}
            showNotifyOnlineDoctor={!isFromNobat}
            activityStatus={isDoctorOnline}
            isDoctorOnline={isDoctorOnline}
            doctorProfileResponse={doctorProfileResponseData}
            doctorId={doctorProfileResponseData?.recId}
            specialityId={doctorProfileResponseData?.latestSpecialityId}
            subLatestSpecialityId={
              doctorProfileResponseData?.subLatestSpecialityId
            }
            systemType={doctorProfileResponseData?.systemType}
            hasSchedule={doctorProfileResponseData?.hasSchedule}
          />
        )}

        <Divider padding={0} className="h-2 bg-surface-secondary mb-3" />

        <div id="aboutDoctor" className="min-h-[90px] scroll-mt-[62px]">
          <ViewMore title="درباره پزشک" aboutDoctor={aboutDoctor} />
        </div>

        <ServicesCarousel
          doctorId={doctorId}
          doctorProfile={doctorProfileResponseData}
        />

        <Insurances recId={doctorId} />

        <Divider padding={0} className="h-2 bg-surface-secondary mb-3" />

        <div id="officeDetail" className="scroll-mt-[62px]">
          <OfficeDetail
            doctorId={String(doctorId)}
            docorName={doctorProfileResponseData?.fullName}
          />
        </div>

        <Divider padding={0} className="h-2 bg-surface-secondary mb-3" />

        {!userServiceDataIsLoading &&
          !!userServiceData?.queryResult?.length && (
            <HealthServices
              items={
                userServiceData?.queryResult?.map(
                  (service) => service.serviceHierarchyName,
                ) ?? []
              }
            />
          )}

        <div
          ref={profileReviewsRef}
          id="doctorReviews"
          className="scroll-mt-[62px]"
        >
          {!!REVIEW_COUNT ? (
            <Comments
              dataPages={doctorProfileReviews.pages.map((page) => page.result)}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              totalReviewsCount={REVIEW_COUNT}
            />
          ) : (
            <div className="w-full flex flex-col gap-y-4 px-4 pb-4">
              <span className="text-Headline/xSmall font-semibold">
                نظر کاربران
              </span>

              <span className="text-Body/xSmall">
                نظری برای این پزشک ثبت نشده است.
              </span>
            </div>
          )}
        </div>

        <Divider padding={0} className="h-2 bg-surface-secondary mb-3" />

        <SuggestedDoctorsCarousel
          doctorId={doctorProfileResponseData?.recId}
          specialityId={doctorProfileResponseData?.latestSpecialityId}
          containerClassName="h-[276px]"
          carouselCardClassName="!h-[212px]"
        />

        <Divider padding={0} className="h-2 bg-surface-secondary mb-3" />

        <DoctorFAQ
          fullName={doctorProfileResponseData?.fullName}
          latestSpecialityName={doctorProfileResponseData?.latestSpecialityName}
          educationLevelId={doctorProfileResponseData?.educationLevelId}
        />
      </div>
    </MainLayout>
  );
};

export default DoctorContainer;
